// /compare/best-crm-for-wedding-videographers.
//
// Read the header of honeybook.js first: every rule there applies here, and so
// do the list rules in honeybookAlternatives.js (disclosure at the top, no single
// winner, Pixally treated like the rest). Sources and exact words for every other
// product: docs/geo/compare-videographer-crm-evidence.md.
//
// Pixally's video pack prices here are videoDeliveryPacks in config/pricingPlans.js
// (USD). If those change, this page changes in the same commit.

export const ROUTE = '/compare/best-crm-for-wedding-videographers';
export const UPDATED_AT = '2026-10-01';
export const CHECKED_AT = 'October 1, 2026';
export const CHECKED_LABEL = 'Other products checked';
// The drawing under the title (lib/compareArt.js).
export const HERO_ART = 'reel';

export const TITLE = 'The best CRM for wedding videographers';
export const SEO_TITLE = 'Best CRM for Wedding Videographers (2026)';
export const SEO_DESCRIPTION =
  'HoneyBook, Dubsado, Studio Ninja, Plutio and Pixally compared for wedding videographers: film delivery, editing, second shooters and price, plus when you still need VidFlow.';

export const LEDE =
  'Dubsado and Studio Ninja, two of the CRMs wedding videographers use most, do not deliver films themselves, so videographers who use them pair them with a delivery platform such as VidFlow. HoneyBook delivers video in its own galleries, up to 2, 8 or 15 hours in total by plan. Pixally, launching on January 1, 2027, is built for wedding videographers and photographers, with film delivery, post-production tracking and second shooters inside the CRM.';

const HB_PRICING = 'https://www.honeybook.com/pricing';
const HB_GALLERIES =
  'https://help.honeybook.com/en/articles/15714707-create-and-share-photo-galleries-in-honeybook';
const HB_ROLES =
  'https://help.honeybook.com/en/articles/6801695-what-permissions-clients-collaborators-and-team-members-have-in-projects';
const DUBSADO_PRICING = 'https://www.dubsado.com/pricing';
const DUBSADO_LINKS =
  'https://help.dubsado.com/en/articles/1796219-share-a-photo-gallery-in-the-client-portal-in-2-0';
const NINJA_PRICING = 'https://www.studioninja.co/pricing/';
const PLUTIO_PRICING = 'https://www.plutio.com/pricing';
const VIDFLOW_PRICING = 'https://vidflow.co/pricing';

export const BODY = [
  {
    type: 'paragraph',
    text: `Pixally is our product, so we have put it on this list and tried to treat the others as fairly as we would want to be treated. Every fact about another product comes from that product's own website, checked ${CHECKED_AT}, and the sources are at the bottom of the page.`,
  },
  { type: 'heading', level: 2, text: 'What a videographer needs from a CRM' },
  { type: 'art', name: 'pipeline' },
  {
    type: 'paragraph',
    text: 'Booking a wedding film looks like booking a wedding album: inquiry, proposal, contract, retainer. The difference comes after the wedding, and it is where most CRMs stop.',
  },
  {
    type: 'list',
    items: [
      'Delivering long films. A feature edit and a ceremony cut can run to hours, and couples keep them for years.',
      'Tracking the edit. Footage, cuts, music, revisions and delivery dates, often across several editors.',
      'Running a crew. Second shooters and editors who work for more than one studio.',
      'Multi-day weddings. A rehearsal, the wedding and a day-after shoot on one booking.',
      'Large payments. Retainers and final balances where a bank-transfer fee is a real amount of money.',
    ],
  },
  { type: 'heading', level: 2, text: 'At a glance' },
  {
    type: 'table',
    label: 'CRMs for wedding videographers compared',
    head: ['', 'Starting price', 'Film delivery', 'Team and second shooters', 'Suits'],
    rows: [
      ['Pixally', 'Free; paid plans from $33 a month billed yearly', 'Built in: 50 GB to start on paid plans, then packs from $50 for 50 GB; no limit on film length', 'Free contractor accounts; second shooter management and a contractor portal on Pro and Studio', 'Film studios with second shooters and editors'],
      ['HoneyBook', '$29 a month billed yearly', 'In its galleries, up to 2, 8 or 15 hours in total by plan', 'Up to 2 team members on Essentials, unlimited on Premium; others join projects as collaborators', 'Solo filmmakers delivering a modest amount of film'],
      ['Dubsado', '$335 a year, or $35 a month', 'Not built in: you link films hosted elsewhere from the client portal', '3 extra users free; 4 to 10 users $25 a month', 'Filmmakers who already deliver through another platform'],
      ['Studio Ninja', '$160 a year, or $16 a month (up to 5 active jobs)', 'Not built in: connects to Pic-Time and ShootProof', '3 users on Pro, unlimited on Master', 'A low-cost CRM for a small studio'],
      ['Plutio', '$19 a month (up to 9 active clients)', 'File storage from 100 GB to 2 TB by plan', 'Core is for one person; Pro includes 30 contributors', 'Businesses that want one general tool for projects and invoicing'],
    ],
  },
  { type: 'heading', level: 2, text: 'The CRMs' },
  { type: 'heading', level: 3, text: 'Pixally (ours, launching January 1, 2027)' },
  {
    type: 'paragraph',
    text: 'Pixally is a CRM built for wedding videographers and photographers. Films are delivered in the CRM, alone or alongside photos, with chapters and no limit on length. Every paid plan starts with 50 GB of film delivery, and more comes in packs of 50 GB for $50, 100 GB for $95 and 500 GB for $450 that never expire. Delivered films stay live for two years and the couple can extend them. Post-production tracking, second shooter management and the contractor portal are on Pro ($58 a month billed yearly) and Studio ($99). It is not available yet: if you need a CRM this season, choose from the others. [How Pixally and HoneyBook compare in detail](/compare/honeybook).',
  },
  { type: 'heading', level: 3, text: 'HoneyBook' },
  {
    type: 'paragraph',
    text: `[HoneyBook](${HB_PRICING}) is client management software for creative and service businesses, from $29 a month billed yearly. Since July 2026 it [delivers video in its own galleries](${HB_GALLERIES}), up to 2 hours on Starter, 8 on Essentials and 15 on Premium, a total for the account rather than per month. Team members are on Essentials (up to 2) and Premium (unlimited); a second shooter can also [join a project as a collaborator](${HB_ROLES}) with their own HoneyBook login.`,
  },
  { type: 'heading', level: 3, text: 'Dubsado' },
  {
    type: 'paragraph',
    text: `[Dubsado](${DUBSADO_PRICING}) costs $35 a month or $335 a year for Starter, and $55 a month or $525 a year for Premier with scheduling and automated workflows. It does not host films: you [add a link to them](${DUBSADO_LINKS}), wherever they are hosted, in the client portal. Three extra users are free.`,
  },
  { type: 'heading', level: 3, text: 'Studio Ninja' },
  {
    type: 'paragraph',
    text: `[Studio Ninja](${NINJA_PRICING}) is a photography business CRM from $16 a month or $160 a year, for one user and up to 5 active jobs; Pro ($27 or $270) has 3 users and unlimited jobs, and Master ($40 or $400) unlimited users and 3 brands. It does not host films itself, and connects to Pic-Time and ShootProof.`,
  },
  { type: 'heading', level: 3, text: 'Plutio' },
  {
    type: 'paragraph',
    text: `[Plutio](${PLUTIO_PRICING}) is general business software for projects, invoices, contracts and scheduling. Core is $19 a month for one person and up to 9 active clients; Pro is $49 with 30 contributors; Max is $199 with unlimited contributors. Plans include 100 GB, 500 GB or 2 TB of file storage, and yearly billing gives 2 months free.`,
  },
  { type: 'heading', level: 2, text: 'When you still need a delivery platform' },
  { type: 'art', name: 'film' },
  {
    type: 'paragraph',
    text: `With Dubsado or Studio Ninja, films go out through a separate platform, such as [VidFlow](${VIDFLOW_PRICING}), which is built for wedding films. VidFlow has no subscription: you buy upload packs of 50 GB for $60, 100 GB for $110 or 500 GB for $475. Couples get two years of access and can extend it, with 4K streaming, downloads and apps for smart TVs. VidFlow says more than 15,000 wedding filmmakers and hybrid teams use it.`,
  },
  {
    type: 'paragraph',
    text: "Pixally's film delivery works the same way, packs and a two-year window the couple can extend, and its packs cost less per gigabyte ($50, $95 and $450 against $60, $110 and $475). The difference is that the delivery lives in the same place as the booking, the contract and the edit. If you choose HoneyBook, you may not need a delivery platform at all until you reach its hours.",
  },
];

export const FAQ = [
  {
    q: 'What is the best CRM for wedding videographers?',
    a: 'It depends on how you deliver films. If you already use a delivery platform such as VidFlow, Dubsado or Studio Ninja handle the booking side. If you want delivery in the CRM today and deliver a modest amount of film, HoneyBook. If you run a crew of second shooters and editors and deliver a lot of film, Pixally, which launches on January 1, 2027.',
  },
  {
    q: 'Can HoneyBook deliver wedding films?',
    a: 'Yes, in its galleries, up to 2 hours of video on Starter, 8 on Essentials and 15 on Premium. That is a total running time for the account, not a monthly allowance.',
  },
  {
    q: 'Do I still need VidFlow if I have a CRM?',
    a: 'With Dubsado or Studio Ninja, yes, or another delivery platform, because they do not deliver films themselves. HoneyBook delivers video up to its hours limit. Pixally delivers films in the CRM from launch.',
  },
  {
    q: 'How much does VidFlow cost?',
    a: 'VidFlow has no subscription. Upload packs cost $60 for 50 GB, $110 for 100 GB and $475 for 500 GB, and include two years of access for your couples, which they can extend.',
  },
  {
    q: 'Is Pixally available yet?',
    a: 'No. Pixally launches on January 1, 2027. You can join the waitlist at pixally.com.',
  },
];

export const SOURCES = [
  { label: 'HoneyBook pricing', url: HB_PRICING },
  { label: 'HoneyBook: create and share photo galleries', url: HB_GALLERIES },
  { label: 'HoneyBook: what clients, collaborators and team members can do in projects', url: HB_ROLES },
  { label: 'HoneyBook support, October 1, 2026: the video limit is "capped by total runtime", not per month', url: null },
  { label: 'Dubsado pricing', url: DUBSADO_PRICING },
  { label: 'Dubsado: add outside links to the client portal', url: DUBSADO_LINKS },
  { label: 'Studio Ninja pricing', url: NINJA_PRICING },
  { label: 'Plutio pricing', url: PLUTIO_PRICING },
  { label: 'VidFlow pricing', url: VIDFLOW_PRICING },
  { label: 'Pixally plans and video packs: the Pixally pricing table', url: null },
];
